import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Switch, Spin, Button } from 'antd';
import { NotificationManager } from 'react-notifications';
import { customBaseUrl, httpGet, httpPost } from '../../../store/http';
import { ArrowLeftOutlined } from '@ant-design/icons';
import Layout from '../../../components/Layout';


const features = [
  { name: 'Loan', enabled: true },
  { name: 'Bill Payment', enabled: true },
  { name: 'USSD', enabled: true },
  { name: 'Card', enabled: true },
  { name: 'Wallet', enabled: true },
  // { name: 'Disbursement', enabled: true },
];

const AppSettings = () => {
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [androidLoanFeatureStatus, setAndroidLoanFeatureStatus] = useState(false);
  const [iosLoanFeatureStatus, setIosLoanFeatureStatus] = useState(false);
  const [fetchingLoanFeatureStatus, setFetchingLoanFeatureStatus] = useState(true);
  const [togglingLoanFeature, setTogglingLoanFeature] = useState(false);

  const fetchLoanFeatureStatus = useCallback(async () => {
    setFetchingLoanFeatureStatus(true);
    try {
      const [androidRes, iosRes] = await Promise.all([
        httpGet(`${customBaseUrl.loanUrl}/api/v1/loans/feature-status?platform=android`),
        httpGet(`${customBaseUrl.loanUrl}/api/v1/loans/feature-status?platform=ios`),
      ]);
      if (androidRes?.status) {
        setAndroidLoanFeatureStatus(androidRes.is_enabled);
      } else {
        NotificationManager.error(androidRes?.message || 'Failed to fetch Android loan feature status.');
      }

      if (iosRes?.status) {
        setIosLoanFeatureStatus(iosRes.is_enabled);
      } else {
        NotificationManager.error(iosRes?.message || 'Failed to fetch iOS loan feature status.');
      }
    } catch (error) {
      NotificationManager.error('Error fetching loan feature statuses.');
      console.error('Error fetching loan feature statuses:', error);
    } finally {
      setFetchingLoanFeatureStatus(false);
    }
  }, []);

  const toggleLoanFeature = async (platform, isEnabled) => {
    setTogglingLoanFeature(true);
    // showLoader(); // Assuming showLoader is available globally or imported
    try {
      const payload = { platform, is_enabled: isEnabled };
      const res = await httpPost(`${customBaseUrl.loanUrl}/api/v1/admin/features/toggle-loan`, payload);

      if (res?.status) {
        NotificationManager.success(`Loan feature for ${platform} ${isEnabled ? 'enabled' : 'disabled'} successfully!`);
        fetchLoanFeatureStatus(); // Re-fetch status to update UI
      } else {
        NotificationManager.error(res?.message || `Failed to toggle loan feature for ${platform}.`);
      }
    } catch (error) {
      NotificationManager.error(`Error toggling loan feature for ${platform}.`);
      console.error(`Error toggling loan feature for ${platform}:`, error);
    } finally {
      setTogglingLoanFeature(false);
      // hideLoader(); // Assuming hideLoader is available globally or imported
    }
  };

  useEffect(() => {
    if (selectedFeature === 'Loan') {
      fetchLoanFeatureStatus();
    }
  }, [selectedFeature, fetchLoanFeatureStatus]);

  return (
    <Layout>
      {selectedFeature ? (
        <div>
          <Button
            type="link"
            onClick={() => setSelectedFeature(null)}
            style={{ marginBottom: 16 }}
            icon={<ArrowLeftOutlined />}
          >
            Back to App Features
          </Button>
          <h2>{selectedFeature} Settings</h2>
          {selectedFeature === 'Loan' && (
            <Spin spinning={fetchingLoanFeatureStatus || togglingLoanFeature} tip="Loading/Toggling Loan Feature...">
              <Card title="Android Loan Feature" style={{ marginBottom: 16 }}>
                <Row align="middle" justify="space-between">
                  <Col>
                    <h3>Enable Loan Feature for Android</h3>
                    <p>Toggle to enable or disable the loan feature on the Android platform.</p>
                  </Col>
                  <Col>
                    <Switch
                      checked={androidLoanFeatureStatus}
                      onChange={(checked) => {
                        setAndroidLoanFeatureStatus(checked);
                        toggleLoanFeature('android', checked);
                      }}
                      disabled={togglingLoanFeature}
                    />
                  </Col>
                </Row>
              </Card>

              <Card title="iOS Loan Feature">
                <Row align="middle" justify="space-between">
                  <Col>
                    <h3>Enable Loan Feature for iOS</h3>
                    <p>Toggle to enable or disable the loan feature on the iOS platform.</p>
                  </Col>
                  <Col>
                    <Switch
                      checked={iosLoanFeatureStatus}
                      onChange={(checked) => {
                        setIosLoanFeatureStatus(checked); // Optimistic update
                        toggleLoanFeature('ios', checked);
                      }}
                      disabled={togglingLoanFeature}
                    />
                  </Col>
                </Row>
              </Card>
            </Spin>
          )}
          {/* Add other feature settings here based on selectedFeature */}
        </div>
      ) : (
        <>
          <h2>App Features</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            {features.map((feature) => (
              <div
                key={feature.name}
                onClick={() => setSelectedFeature(feature.name)}
                style={{
                  border: '1px solid #ccc',
                  padding: '1rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: feature.enabled ? '#e6ffe6' : '#ffe6e6',
                }}
              >
                <h3>{feature.name}</h3>
                <p />
                {/* <p>Status: {feature.enabled ? 'Enabled' : 'Disabled'}</p> */}
              </div>
            ))}
          </div>
        </>
      )}
    </Layout>
  );
};

export default AppSettings;
